import React, { useState, useMemo } from 'react'
import {
    useTheme,
    TableSortLabel,
    Typography,
    CircularProgress,
    Chip,
    IconButton,
    Collapse,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@material-ui/core'
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import { Pagination } from '@material-ui/lab'
import { format } from 'date-fns'
import { Container } from '../../components'
import { usePagination } from '../../hooks'
import { MoveInSurveyDetail } from '../../hooks/useMoveInSurvey'
import { ratingColor } from './PortfolioTable'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    detail: MoveInSurveyDetail | null
    loading: boolean
    searchText?: string
}

const UnitASC = 'UnitASC'
const UnitDESC = 'UnitDESC'
const RatingASC = 'RatingASC'
const RatingDESC = 'RatingDESC'
const StatusASC = 'StatusASC'
const StatusDESC = 'StatusDESC'
const DateASC = 'DateASC'
const DateDESC = 'DateDESC'

const SERVICE_STATUS_DISPLAY: Record<
    string,
    { label: string; color: string }
> = {
    fully_serviced: { label: 'Fully Serviced', color: '#4caf50' },
    incomplete: { label: 'Incomplete', color: '#ff9800' },
    not_tracked: { label: 'No Service', color: '#9e9e9e' },
}

export const SurveyDetailContent: React.FC<Props> = ({
    detail,
    loading,
    searchText = '',
}) => {
    const theme = useTheme()
    const [orderBy, setOrderBy] = useState('')
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

    const toggleRow = (id: number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const hasAnyAnswers = useMemo(() => {
        if (!detail?.surveys) return false
        return detail.surveys.some((s) => s.answers?.length > 0)
    }, [detail?.surveys])

    const sortedSurveys = useMemo(() => {
        if (!detail?.surveys) return []
        let list = [...detail.surveys]

        if (searchText.trim()) {
            const query = searchText.trim().toLowerCase()
            list = list.filter(
                (s) =>
                    s.unit_name.toLowerCase().includes(query) ||
                    s.submitted_by_name.toLowerCase().includes(query) ||
                    s.submitted_by_email.toLowerCase().includes(query) ||
                    (s.comment && s.comment.toLowerCase().includes(query)),
            )
        }

        switch (orderBy) {
            case UnitASC:
                list.sort((a, b) => a.unit_name.localeCompare(b.unit_name))
                break
            case UnitDESC:
                list.sort((a, b) => b.unit_name.localeCompare(a.unit_name))
                break
            case RatingASC:
                list.sort((a, b) => a.rating - b.rating)
                break
            case RatingDESC:
                list.sort((a, b) => b.rating - a.rating)
                break
            case StatusASC:
                list.sort((a, b) =>
                    (a.service_status || '').localeCompare(
                        b.service_status || '',
                    ),
                )
                break
            case StatusDESC:
                list.sort((a, b) =>
                    (b.service_status || '').localeCompare(
                        a.service_status || '',
                    ),
                )
                break
            case DateASC:
                list.sort(
                    (a, b) =>
                        new Date(a.created_date).getTime() -
                        new Date(b.created_date).getTime(),
                )
                break
            case DateDESC:
                list.sort(
                    (a, b) =>
                        new Date(b.created_date).getTime() -
                        new Date(a.created_date).getTime(),
                )
                break
        }
        return list
    }, [detail?.surveys, orderBy, searchText])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        sortedSurveys,
    )

    const toggleSort = (ascKey: string, descKey: string) => {
        setOrderBy((prev) => (prev !== ascKey ? ascKey : descKey))
    }

    if (loading) {
        return (
            <Container
                flex={1}
                alignItems="center"
                justifyContent="center"
                style={{ padding: theme.spacing(6, 0) }}
            >
                <CircularProgress size={60} />
            </Container>
        )
    }

    if (!detail) {
        return (
            <Container
                alignItems="center"
                justifyContent="center"
                style={{ padding: theme.spacing(6, 0) }}
            >
                <Typography variant="body1" color="textSecondary">
                    No survey data available.
                </Typography>
            </Container>
        )
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <TableContainer style={{ flex: 1 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {hasAnyAnswers && (
                                <TableCell
                                    style={{
                                        fontWeight: 600,
                                        width: 40,
                                        padding: 0,
                                    }}
                                />
                            )}
                            <TableCell
                                style={{
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                }}
                                onClick={() => toggleSort(UnitASC, UnitDESC)}
                            >
                                <TableSortLabel
                                    active={
                                        orderBy === UnitASC ||
                                        orderBy === UnitDESC
                                    }
                                    direction={
                                        orderBy === UnitASC ? 'asc' : 'desc'
                                    }
                                >
                                    Unit
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{ fontWeight: 600 }}>
                                Area
                            </TableCell>
                            <TableCell style={{ fontWeight: 600 }}>
                                Tenant Name
                            </TableCell>
                            <TableCell style={{ fontWeight: 600 }}>
                                Tenant Email
                            </TableCell>
                            <TableCell
                                style={{
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                }}
                                onClick={() =>
                                    toggleSort(RatingASC, RatingDESC)
                                }
                            >
                                <TableSortLabel
                                    active={
                                        orderBy === RatingASC ||
                                        orderBy === RatingDESC
                                    }
                                    direction={
                                        orderBy === RatingASC ? 'asc' : 'desc'
                                    }
                                >
                                    Rating
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                style={{
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={() =>
                                    toggleSort(StatusASC, StatusDESC)
                                }
                            >
                                <TableSortLabel
                                    active={
                                        orderBy === StatusASC ||
                                        orderBy === StatusDESC
                                    }
                                    direction={
                                        orderBy === StatusASC ? 'asc' : 'desc'
                                    }
                                >
                                    Unit Ready
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                style={{
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={() => toggleSort(DateASC, DateDESC)}
                            >
                                <TableSortLabel
                                    active={
                                        orderBy === DateASC ||
                                        orderBy === DateDESC
                                    }
                                    direction={
                                        orderBy === DateASC ? 'asc' : 'desc'
                                    }
                                >
                                    Submitted Time
                                </TableSortLabel>
                            </TableCell>
                            <TableCell style={{ fontWeight: 600 }}>
                                Comments
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageData.length > 0 ? (
                            pageData.map((survey) => {
                                const surveyHasAnswers =
                                    survey.answers?.length > 0
                                const isExpanded = expandedRows.has(survey.id)
                                const colCount = hasAnyAnswers ? 9 : 8

                                return (
                                    <React.Fragment key={survey.id}>
                                        <TableRow
                                            hover
                                            style={{
                                                cursor: surveyHasAnswers
                                                    ? 'pointer'
                                                    : undefined,
                                            }}
                                            onClick={() =>
                                                surveyHasAnswers &&
                                                toggleRow(survey.id)
                                            }
                                        >
                                            {hasAnyAnswers && (
                                                <TableCell
                                                    style={{
                                                        width: 40,
                                                        padding: theme.spacing(
                                                            0,
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    {surveyHasAnswers && (
                                                        <IconButton size="small">
                                                            {isExpanded ? (
                                                                <KeyboardArrowUp fontSize="small" />
                                                            ) : (
                                                                <KeyboardArrowDown fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                {survey.unit_name}
                                            </TableCell>
                                            <TableCell>
                                                {survey.area_name}
                                            </TableCell>
                                            <TableCell>
                                                {survey.submitted_by_name}
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {survey.submitted_by_email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={survey.rating}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: ratingColor(
                                                            survey.rating,
                                                            theme.palette,
                                                        ),
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        minWidth: 32,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const status =
                                                        SERVICE_STATUS_DISPLAY[
                                                            survey
                                                                .service_status
                                                        ]
                                                    return status ? (
                                                        <Chip
                                                            label={status.label}
                                                            size="small"
                                                            style={{
                                                                backgroundColor:
                                                                    status.color,
                                                                color: '#fff',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            —
                                                        </Typography>
                                                    )
                                                })()}
                                            </TableCell>
                                            <TableCell
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {format(
                                                        new Date(
                                                            survey.created_date,
                                                        ),
                                                        'MM/dd/yyyy h:mm a',
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    maxWidth: 300,
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {survey.comment || (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {surveyHasAnswers && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={colCount}
                                                    style={{
                                                        padding: 0,
                                                        borderBottom: isExpanded
                                                            ? undefined
                                                            : 'none',
                                                    }}
                                                >
                                                    <Collapse
                                                        in={isExpanded}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <div
                                                            style={{
                                                                padding: theme.spacing(
                                                                    1.5,
                                                                    3,
                                                                    1.5,
                                                                    hasAnyAnswers
                                                                        ? 7
                                                                        : 3,
                                                                ),
                                                                backgroundColor:
                                                                    theme
                                                                        .palette
                                                                        .grey[50],
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                style={{
                                                                    fontWeight: 600,
                                                                    marginBottom: theme.spacing(
                                                                        1,
                                                                    ),
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .text
                                                                            .secondary,
                                                                }}
                                                            >
                                                                Custom Questions
                                                            </Typography>
                                                            {survey.answers.map(
                                                                (a, i) => (
                                                                    <div
                                                                        key={i}
                                                                        style={{
                                                                            display:
                                                                                'flex',
                                                                            gap: theme.spacing(
                                                                                1,
                                                                            ),
                                                                            padding: theme.spacing(
                                                                                0.5,
                                                                                0,
                                                                            ),
                                                                            borderBottom:
                                                                                i <
                                                                                survey
                                                                                    .answers
                                                                                    .length -
                                                                                    1
                                                                                    ? `1px solid ${theme.palette.divider}`
                                                                                    : 'none',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            style={{
                                                                                flex: 1,
                                                                                color:
                                                                                    theme
                                                                                        .palette
                                                                                        .text
                                                                                        .secondary,
                                                                            }}
                                                                        >
                                                                            {
                                                                                a.question_text
                                                                            }
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            style={{
                                                                                fontWeight: 500,
                                                                            }}
                                                                        >
                                                                            {a.value ||
                                                                                '—'}
                                                                        </Typography>
                                                                    </div>
                                                                ),
                                                            )}
                                                        </div>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                )
                            })
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={hasAnyAnswers ? 9 : 8}
                                    align="center"
                                >
                                    <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        style={{
                                            padding: theme.spacing(4, 0),
                                        }}
                                    >
                                        No survey responses found.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {sortedSurveys.length > 0 && (
                <Container
                    style={{
                        ...typographyStyle(theme.typography.subtitle2),
                        borderTop: `1px solid ${theme.palette.grey[300]}`,
                        padding: theme.spacing(1, 2),
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: theme.spacing(2),
                    }}
                >
                    <Pagination
                        count={numPages}
                        page={page}
                        onChange={(_: unknown, newPage: number) => {
                            setPage(newPage)
                        }}
                        size="small"
                    />
                    <Typography variant="body2" color="textSecondary">
                        {start} - {end} of {sortedSurveys.length}
                    </Typography>
                </Container>
            )}
        </div>
    )
}
